import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { getIdToken } from '../attendedUtils';
import { useIsMobile } from '../hooks/useMediaQuery';
import { SearchableSelect } from './MyProfile';
import { MemberSearchSelect } from './ArtistMembersTab';
import { HomeTitle, HomeIntroPillars } from './Home';

// Password-manager opt-out (2026-10-19): 1Password offered personal autofill
// on these fields. Same attribute set as the 2026-10-09 name-field fix.
const NO_AUTOFILL = {
    autoComplete: 'off',
    'data-1p-ignore': 'true',
    'data-lpignore': 'true',
    'data-bwignore': 'true',
    'data-form-type': 'other',
};

// ── OnboardingWizard ────────────────────────────────────────────────────────
// Mandatory 4-screen flow shown to newly invited users on first login, after
// they've accepted Terms (see AccountGate in App.js).
// (2026-10-19) Screen 1 is a new Welcome screen — the Home page's
// "sharc.fm | Show Archives" title and three pillars, nothing saved. Then
// Screen 2 User Information (required), Screen 3 Member record and Screen 4
// Public Profile (both skippable). Progress saves to the DB after every screen
// via PUT /user, so a closed tab mid-flow doesn't lose earlier answers.
// Spec: SHARC_Onboarding_Spec_2026-09-01.docx
//
// `onComplete` is called once onboarding_completed_at is set server-side,
// so AccountGate can drop the wizard and render the app.
//
// (2026-10-22) Resume after an abandoned wizard. `initialUser` is AccountGate's
// GET /user row. Screen 2 saves username + display name + location in one PUT,
// so a saved username means Screen 2 is already done: Welcome's Continue goes
// straight to Screen 3, and Screens 2–3 are pre-filled from the saved row (Back
// still reaches Screen 2 to edit). The username check excludes the user's own
// id, so their saved username reads as available instead of "Already taken".
function OnboardingWizard({ initialUser, onComplete }) {
    const isMobile = useIsMobile();
    const savedUsername = (initialUser && initialUser.username) || '';

    const [step, setStep] = useState(1);
    const [picklists, setPicklists] = useState(null);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Screen 1 form state
    const [form, setForm] = useState({
        username: savedUsername,
        display_name: (initialUser && initialUser.display_name) || '',
        city: (initialUser && initialUser.city) || '',
        country_id: '',
        state_id: '',
    });
    const [usernameStatus, setUsernameStatus] = useState(null); // null | checking | available | taken | invalid

    // Screen 2 form state
    // Pre-filled on resume so a previously claimed Member record isn't lost
    // (Screen 3's Skip would otherwise save member_id = null over it).
    const [hasPerformed, setHasPerformed] = useState(initialUser && initialUser.member_id ? true : null); // null | true | false
    const [memberId, setMemberId] = useState(initialUser && initialUser.member_id ? String(initialUser.member_id) : '');

    // Screen 3 form state
    const [isPublic, setIsPublic] = useState(true);

    useEffect(() => {
        async function loadAll() {
            try {
                const [picklistsRes, membersRes] = await Promise.all([
                    axios.get(`${config.API_URL}/picklists`),
                    axios.get(`${config.API_URL}/members`),
                ]);
                setPicklists(picklistsRes.data);
                setMembers(membersRes.data.members || membersRes.data || []);

                // Resume: users.country / users.state hold codes; map them back
                // to picklist ids. State codes repeat across countries, so the
                // state is matched within the saved country only.
                if (initialUser && initialUser.country) {
                    const countryObj = (picklistsRes.data.countries || []).find(c => c.code === initialUser.country);
                    if (countryObj) {
                        const stateObj = initialUser.state
                            ? (picklistsRes.data.states_regions || []).find(s => s.country_id === countryObj.id && s.code === initialUser.state)
                            : null;
                        setForm(prev => ({
                            ...prev,
                            country_id: String(countryObj.id),
                            state_id: stateObj ? String(stateObj.id) : '',
                        }));
                    }
                }
                setLoading(false);
            } catch (e) {
                setError('Failed to load. Please refresh and try again.');
                setLoading(false);
            }
        }
        loadAll();
    }, [initialUser]);

    const filteredStates = picklists && form.country_id
        ? picklists.states_regions.filter(s => s.country_id === parseInt(form.country_id))
        : [];
    const stateRequired = filteredStates.length > 0;

    const handleFormChange = (field, value) => {
        setForm(prev => ({
            ...prev,
            [field]: value,
            ...(field === 'country_id' ? { state_id: '' } : {}),
        }));
    };

    // Username availability check (debounced) — same pattern as My Profile.
    // (2026-10-22) exclude_id = the user's own id, so a username they already
    // saved on an earlier, abandoned attempt isn't reported as taken.
    useEffect(() => {
        const candidate = form.username.trim().toLowerCase();
        if (!candidate) { setUsernameStatus(null); return; }
        const valid = /^[a-z0-9-]{3,30}$/.test(candidate);
        if (!valid) { setUsernameStatus('invalid'); return; }
        setUsernameStatus('checking');
        const timer = setTimeout(() => {
            const params = { username: candidate };
            if (initialUser && initialUser.id) params.exclude_id = initialUser.id;
            axios.get(`${config.API_URL}/check-username`, { params })
                .then(res => setUsernameStatus(res.data.available ? 'available' : 'taken'))
                .catch(() => setUsernameStatus(null));
        }, 500);
        return () => clearTimeout(timer);
    }, [form.username, initialUser]);

    const screen1Valid =
        usernameStatus === 'available' &&
        form.display_name.trim() &&
        form.city.trim() &&
        form.country_id &&
        (!stateRequired || form.state_id);

    // ── Save helpers ──────────────────────────────────────────────────────────
    async function saveUser(payload) {
        const token = await getIdToken();
        return axios.put(`${config.API_URL}/user`, payload, {
            headers: { Authorization: token }
        });
    }

    const handleScreen1Continue = async () => {
        if (!screen1Valid) return;
        setSaving(true);
        setError(null);
        try {
            const countryObj = picklists.countries.find(c => String(c.id) === form.country_id);
            const stateObj = form.state_id
                ? picklists.states_regions.find(s => String(s.id) === form.state_id)
                : null;

            await saveUser({
                display_name: form.display_name.trim(),
                city: form.city.trim(),
                country: countryObj ? countryObj.code : null,
                state: stateObj ? stateObj.code : null,
                username: form.username.trim().toLowerCase(),
                is_public: true,
            });
            setStep(3);
        } catch (e) {
            const serverMessage = e.response && e.response.data && e.response.data.message;
            setError(serverMessage || 'Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleScreen2Advance = async (skip) => {
        setSaving(true);
        setError(null);
        try {
            const idToSave = skip ? null : (hasPerformed && memberId ? parseInt(memberId) : null);
            await saveUser({ member_id: idToSave });
            setStep(4);
        } catch (e) {
            setError('Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleFinish = async (skip) => {
        setSaving(true);
        setError(null);
        try {
            const payload = { action: 'completeOnboarding' };
            if (!skip) payload.is_public = isPublic;
            const res = await saveUser(payload);
            onComplete(res.data);
        } catch (e) {
            setError('Failed to save. Please try again.');
            setSaving(false);
        }
    };

    const ms = {
        // Solid (2026-10-19): no page or site background photo behind the wizard
        overlay: { position: 'fixed', inset: 0, backgroundColor: '#121212', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: isMobile ? '12px' : '20px' },
        modal: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '12px', width: '100%', maxWidth: step === 1 ? '960px' : '480px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.7)' },
        // Welcome screen title — the Home page header's type treatment
        brandTitle: { margin: '0 0 8px', fontSize: '18px', letterSpacing: '0.06em', textTransform: 'uppercase' },
        header: { padding: isMobile ? '24px 22px 16px' : '32px 32px 20px', borderBottom: '1px solid #2a2a2a' },
        step: { fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '10px' },
        title: { margin: '0 0 8px', fontSize: '20px', fontWeight: 'bold', color: '#e0e0e0' },
        subtitle: { margin: 0, fontSize: '14px', color: '#888', lineHeight: 1.5 },
        body: { padding: isMobile ? '20px 22px' : '24px 32px' },
        fieldGroup: { marginBottom: '18px' },
        label: { display: 'block', fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
        input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '10px 14px', fontSize: '15px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box', outline: 'none' },
        fieldRow: { display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '18px' : '16px', marginBottom: '18px' },
        sharcChecking: { fontSize: '13px', color: '#888', marginTop: '6px' },
        sharcAvailable: { fontSize: '13px', color: '#4caf7d', marginTop: '6px' },
        sharcTaken: { fontSize: '13px', color: '#cf6679', marginTop: '6px' },
        segRow: { display: 'flex', gap: '10px', marginBottom: '18px' },
        segBtn: (active) => ({
            flex: 1, padding: '12px', borderRadius: '8px', textAlign: 'center', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
            border: active ? '1px solid #bb86fc' : '1px solid #444',
            backgroundColor: active ? '#2a2040' : '#252525',
            color: active ? '#bb86fc' : '#ccc',
        }),
        caveat: { fontSize: '12px', color: '#888', lineHeight: 1.6, backgroundColor: '#161616', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '12px 14px', marginTop: '4px', marginBottom: '4px' },
        privacyToggleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', marginBottom: '8px' },
        privacyToggleLabel: { fontSize: '14px', color: '#e0e0e0', fontWeight: 'bold', marginBottom: '4px' },
        privacyToggleHint: { fontSize: '12px', color: '#888' },
        switchTrack: { width: '44px', height: '24px', borderRadius: '12px', border: 'none', position: 'relative', cursor: 'pointer', flexShrink: 0, padding: 0, transition: 'background-color 0.15s ease' },
        switchKnob: { position: 'absolute', top: '2px', left: 0, width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#f0f0f0', transition: 'transform 0.15s ease', display: 'block' },
        error: { marginTop: '4px', marginBottom: '12px', fontSize: '13px', color: '#e57373' },
        footer: { padding: isMobile ? '0 22px 22px' : '0 32px 32px', display: 'flex', flexDirection: 'column', gap: '10px' },
        primaryBtn: { width: '100%', padding: '14px', backgroundColor: '#bb86fc', border: 'none', borderRadius: '8px', color: '#121212', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' },
        footerRow: { display: 'flex', gap: '10px' },
        backBtn: { padding: '14px 18px', backgroundColor: 'transparent', border: '1px solid #444', borderRadius: '8px', color: '#ccc', fontSize: '14px', cursor: 'pointer' },
        skipLink: { textAlign: 'center', background: 'none', border: 'none', color: '#888', fontSize: '13px', cursor: 'pointer', padding: '4px', textDecoration: 'underline' },
    };

    if (loading) {
        return (
            <div style={ms.overlay}>
                <div style={{ color: '#888', fontSize: '14px' }}>Loading…</div>
            </div>
        );
    }

    return (
        <div style={ms.overlay}>
            <div style={ms.modal}>
                <div style={ms.header}>
                    <div style={ms.step}>Step {step} of 4</div>
                    {step === 1 && <>
                        <h2 style={{ ...ms.brandTitle, textAlign: 'center' }}><HomeTitle /></h2>
                    </>}
                    {step === 2 && <>
                        <h2 style={ms.brandTitle}><HomeTitle /></h2>
                        <p style={ms.subtitle}>Let's get your account set up. This only takes a minute.</p>
                    </>}
                    {step === 3 && <>
                        <h2 style={ms.title}>Have you played shows?</h2>
                        <p style={ms.subtitle}>If you have performed as part of an artist (band, DJ, etc), you can track the members of the artist for specific shows.  Additionally, you can link your sharc.fm user record to a member record which helps track shows you’ve performed.</p>
                    </>}
                    {step === 4 && <>
                        <h2 style={ms.title}>Would you like other users to see shows you have attended?</h2>
                    </>}
                </div>

                <div style={ms.body}>
                    {/* ── Screen 1: Welcome (2026-10-19) ── */}
                    {step === 1 && <HomeIntroPillars isMobile={isMobile} />}

                    {/* ── Screen 2: User Information ── */}
                    {step === 2 && (
                        <>
                            <div style={ms.fieldGroup}>
                                <label style={ms.label}>Your Username</label>
                                <input
                                    type="text"
                                    value={form.username}
                                    onChange={e => handleFormChange('username', e.target.value.toLowerCase())}
                                    placeholder="Your Username"
                                    {...NO_AUTOFILL}
                                    style={{
                                        ...ms.input,
                                        borderColor: usernameStatus === 'taken' || usernameStatus === 'invalid' ? '#cf6679'
                                            : usernameStatus === 'available' ? '#4caf7d' : '#444'
                                    }}
                                    autoFocus
                                />
                                {usernameStatus === 'checking' && <div style={ms.sharcChecking}>Checking availability…</div>}
                                {usernameStatus === 'available' && <div style={ms.sharcAvailable}>✓ Available — sharc.fm/u/{form.username.trim().toLowerCase()}</div>}
                                {usernameStatus === 'taken' && <div style={ms.sharcTaken}>✕ Already taken</div>}
                                {usernameStatus === 'invalid' && <div style={ms.sharcTaken}>✕ 3-30 characters: lowercase letters, numbers, and dashes only</div>}
                            </div>

                            <div style={ms.fieldGroup}>
                                <label style={ms.label}>Your Display Name</label>
                                <input
                                    type="text"
                                    value={form.display_name}
                                    onChange={e => handleFormChange('display_name', e.target.value)}
                                    placeholder="Your name"
                                    style={ms.input}
                                    {...NO_AUTOFILL}
                                />
                            </div>

                            <div style={ms.fieldGroup}>
                                <label style={ms.label}>Your Location — City</label>
                                <input
                                    type="text"
                                    value={form.city}
                                    onChange={e => handleFormChange('city', e.target.value)}
                                    placeholder="Your city"
                                    style={ms.input}
                                    {...NO_AUTOFILL}
                                />
                            </div>

                            <div style={ms.fieldRow}>
                                <div style={{ flex: 1 }}>
                                    <label style={ms.label}>Country</label>
                                    {picklists && (
                                        <SearchableSelect
                                            options={picklists.countries}
                                            value={form.country_id}
                                            onChange={val => handleFormChange('country_id', val)}
                                            placeholder="Select country..."
                                            renderLabel={c => c.name}
                                        />
                                    )}
                                </div>
                                {stateRequired && (
                                    <div style={{ flex: 1 }}>
                                        <label style={ms.label}>Region / State</label>
                                        <SearchableSelect
                                            options={filteredStates}
                                            value={form.state_id}
                                            onChange={val => handleFormChange('state_id', val)}
                                            placeholder="Select state..."
                                            renderLabel={s => s.name}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* ── Screen 3: Member Information ── */}
                    {step === 3 && (
                        <>
                            <div style={ms.segRow}>
                                <div style={ms.segBtn(hasPerformed === true)} onClick={() => setHasPerformed(true)}>Yes</div>
                                <div style={ms.segBtn(hasPerformed === false)} onClick={() => { setHasPerformed(false); setMemberId(''); }}>No</div>
                            </div>

                            {hasPerformed === true && (
                                <>
                                    <div style={ms.fieldGroup}>
                                        <label style={ms.label}>Search for your Member record</label>
                                        <MemberSearchSelect
                                            value={memberId}
                                            allMembers={members}
                                            onChange={setMemberId}
                                            onMemberCreated={(m) => setMembers(prev => [...prev, m])}
                                        />
                                    </div>
                                    <div style={ms.caveat}>
                                        Shows where you're added as a performing member of an artist will appear
                                        in your My Shows area as <strong>Performed</strong>. Please only link your
                                        account to a Member record that's actually you — this is subject to our
                                        Terms &amp; Conditions.
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* ── Screen 4: Public Profile ── */}
                    {step === 4 && (
                        <div style={ms.privacyToggleRow}>
                            <div>
                                <div style={ms.privacyToggleLabel}>Public Profile</div>
                                <div style={ms.privacyToggleHint}>
                                    {isPublic
                                        ? 'Other logged-in users can view your show history.'
                                        : 'Only you can view your show history.'}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsPublic(v => !v)}
                                style={{ ...ms.switchTrack, backgroundColor: isPublic ? '#bb86fc' : '#444' }}
                                aria-pressed={isPublic}
                            >
                                <span style={{ ...ms.switchKnob, transform: isPublic ? 'translateX(20px)' : 'translateX(2px)' }} />
                            </button>
                        </div>
                    )}

                    {error && <div style={ms.error}>{error}</div>}
                </div>

                <div style={ms.footer}>
                    {step === 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button onClick={() => setStep(savedUsername ? 3 : 2)} style={{ ...ms.primaryBtn, maxWidth: '416px' }}>Continue</button>
                        </div>
                    )}

                    {step === 2 && (
                        <div style={ms.footerRow}>
                            <button style={ms.backBtn} onClick={() => setStep(1)} disabled={saving}>Back</button>
                        <button
                            onClick={handleScreen1Continue}
                            disabled={!screen1Valid || saving}
                            style={{ ...ms.primaryBtn, opacity: (!screen1Valid || saving) ? 0.6 : 1, cursor: (!screen1Valid || saving) ? 'not-allowed' : 'pointer' }}
                        >
                            {saving ? 'Saving…' : 'Continue'}
                        </button>
                        </div>
                    )}

                    {step === 3 && (
                        <>
                            <div style={ms.footerRow}>
                                <button style={ms.backBtn} onClick={() => setStep(2)} disabled={saving}>Back</button>
                                <button
                                    onClick={() => handleScreen2Advance(false)}
                                    disabled={saving || hasPerformed === null || (hasPerformed === true && !memberId)}
                                    style={{
                                        ...ms.primaryBtn,
                                        opacity: (saving || hasPerformed === null || (hasPerformed === true && !memberId)) ? 0.6 : 1,
                                        cursor: (saving || hasPerformed === null || (hasPerformed === true && !memberId)) ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    {saving ? 'Saving…' : 'Continue'}
                                </button>
                            </div>
                            <button style={ms.skipLink} onClick={() => handleScreen2Advance(true)} disabled={saving}>Skip for now</button>
                        </>
                    )}

                    {step === 4 && (
                        <>
                            <div style={ms.footerRow}>
                                <button style={ms.backBtn} onClick={() => setStep(3)} disabled={saving}>Back</button>
                                <button
                                    onClick={() => handleFinish(false)}
                                    disabled={saving}
                                    style={{ ...ms.primaryBtn, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
                                >
                                    {saving ? 'Saving…' : 'Finish'}
                                </button>
                            </div>
                            <button style={ms.skipLink} onClick={() => handleFinish(true)} disabled={saving}>Skip for now</button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default OnboardingWizard;
